import { supabase } from './supabase';

const BUCKET = 'halloween-school-gallery';
const MAX_SIZE = 6 * 1024 * 1024;
const MIME_EXT = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };
const COLUMNS = 'id,storage_path,title,description,alt_text,school_label,event_year,sort_order,is_published,created_at,updated_at';

function galleryError(error, fallback) {
  return new Error(error?.message || fallback);
}
function withUrl(row) {
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(row.storage_path);
  return { ...row, imageUrl: data?.publicUrl || '' };
}
export async function listHalloweenGallery({ admin = false } = {}) {
  let request = supabase.from('halloween_school_gallery').select(admin ? COLUMNS : 'id,storage_path,title,description,alt_text,school_label,event_year,sort_order,created_at');
  if (!admin) request = request.eq('is_published', true);
  const { data, error } = await request.order('sort_order', { ascending: true }).order('created_at', { ascending: false }).limit(100);
  if (error) throw galleryError(error, 'The school photo gallery is temporarily unavailable.');
  return (data || []).map(withUrl);
}
async function checkMagic(file) {
  const bytes = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  if (file.type === 'image/jpeg') return bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
  if (file.type === 'image/png') return bytes[0] === 137 && bytes[1] === 80 && bytes[2] === 78 && bytes[3] === 71;
  if (file.type === 'image/webp') return String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF' && String.fromCharCode(...bytes.slice(8, 12)) === 'WEBP';
  return false;
}
function cleanDetails(details) {
  const year = Number(details.event_year);
  if (!Number.isInteger(year) || year < 2018 || year > 2100) throw new Error('Please select a valid event year.');
  const title = String(details.title || '').trim().slice(0, 160);
  const alt = String(details.alt_text || '').trim().slice(0, 220);
  if (title.length < 2 || alt.length < 3) throw new Error('A photo title and a meaningful image description are required.');
  return {
    title,
    alt_text: alt,
    description: String(details.description || '').trim().slice(0, 1000),
    school_label: String(details.school_label || '').trim().slice(0, 120),
    event_year: year,
    sort_order: Math.max(-10000, Math.min(10000, Number(details.sort_order) || 0)),
    is_published: details.is_published === true,
  };
}
export async function uploadHalloweenPhoto(file, details) {
  if (!(file instanceof File)) throw new Error('Select a real photograph to upload.');
  if (!Object.prototype.hasOwnProperty.call(MIME_EXT, file.type) || file.size < 1 || file.size > MAX_SIZE || !(await checkMagic(file))) {
    throw new Error('Photos must be valid JPG, PNG or WebP files, no larger than 6 MB.');
  }
  const { data: identity, error: authError } = await supabase.auth.getUser();
  if (authError || !identity?.user?.id) throw new Error('Sign in as the site administrator before uploading.');
  const path = 'schools/' + crypto.randomUUID() + '.' + MIME_EXT[file.type];
  const storage = supabase.storage.from(BUCKET);
  const { error: uploadError } = await storage.upload(path, file, {
    contentType: file.type,
    cacheControl: '3600',
    upsert: false,
  });
  if (uploadError) throw galleryError(uploadError, 'Photo upload failed.');
  const { data, error } = await supabase.from('halloween_school_gallery')
    .insert({ ...cleanDetails(details), storage_path: path, uploaded_by: identity.user.id })
    .select(COLUMNS).single();
  if (error) {
    await storage.remove([path]);
    throw galleryError(error, 'The uploaded photo could not be saved in the gallery.');
  }
  return withUrl(data);
}
export async function updateHalloweenPhoto(id, details) {
  const { data, error } = await supabase.from('halloween_school_gallery')
    .update({ ...cleanDetails(details), updated_at: new Date().toISOString() })
    .eq('id', id).select(COLUMNS).single();
  if (error) throw galleryError(error, 'Could not save photo details.');
  return withUrl(data);
}
export async function deleteHalloweenPhoto(row) {
  const { error } = await supabase.from('halloween_school_gallery')
    .delete().eq('id', row.id).select('id').single();
  if (error) throw galleryError(error, 'Could not remove the gallery entry.');
  const { error: storageError } = await supabase.storage.from(BUCKET).remove([row.storage_path]);
  if (storageError) throw galleryError(storageError, 'Entry removed, but its original image could not be deleted from storage. Please contact administration.');
}
